<template>
    <div class="tab-pane active">
        <div v-if="roomProfilesCopy.length > 0" class="table-wrapper no-print">
            <table
                v-show="!searchKeyword || filteredRoomProfiles.length"
                id="forAT_table-RoomProfiles"
                class="common-table room-profiles"
            >
                <thead>
                    <tr class="tbl-header">
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.location') }}</th>
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.profile') }}</th>
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.description') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(room, index) in filteredRoomProfiles" :key="index">
                        <td :title="room.name">{{ room.name }}</td>
                        <td>
                            <SelectBox
                                v-model="room.sensor_profile"
                                :options="profileSelectOptions"
                                :placeholder="
                                    !room.sensor_profile ? $t('sensors.roomProfilesTab.noProfile') : ''
                                "
                            />
                        </td>
                        <td>{{ room.sensor_profile ? room.sensor_profile.description : '' }}</td>
                    </tr>
                </tbody>
            </table>
            <h3 v-if="searchKeyword && filteredRoomProfiles.length === 0">
                {{ $t('common.searchNoResult', { val: searchKeyword }) }}
            </h3>
        </div>
        <div v-else class="data-empty">
            <h3 v-if="dataLoaded" v-text="$t('sensors.roomProfilesTab.noRoomProfiles')"></h3>
        </div>
        <Transition name="slide-fade">
            <div
                v-if="roomProfilesCopy.length === 0 && dataLoaded"
                class="full-window-info icon-allert no-print"
            >
                {{ $t('sensors.roomProfilesTab.noRoomProfilesInfo') }}
            </div>
        </Transition>
        <div class="print-only">
            <h4 v-if="searchKeyword && filteredRoomProfiles.length > 0">
                {{ $t('print.search', { val: searchKeyword }) }}
            </h4>
            <table
                v-if="roomProfilesCopy.length > 0"
                id="forAT_table-RoomProfiles-print"
                class="table print-room-profiles"
            >
                <thead>
                    <tr class="tbl-header">
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.location') }}</th>
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.profile') }}</th>
                        <th>{{ $t('sensors.roomProfilesTab.table.columns.description') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(room, index) in filteredRoomProfilesCopy" :key="index">
                        <td>{{ room.name }}</td>
                        <td>
                            {{
                                room.sensor_profile
                                    ? profilesCopy[room.sensor_profile.sensor_profile_id]
                                    : profilesCopy[0]
                            }}
                        </td>
                        <td>{{ room.sensor_profile ? room.sensor_profile.description : '' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapState, mapMutations } from 'vuex';
import isEqual from 'lodash-es/isEqual';
import { resourceRoomProfiles, resourceResidentProfiles } from '../../api/resources';
import { getCancelToken } from '../../api';
import SelectBox from '../common/SelectBox.vue';
import mappingState from '../common/mappingState';
import { errorHandler, sortLocations, ifIsNotCanceled } from '../common/helpers';
import { SUCCESS, ERROR, ROUTE_NAMES, USER_TYPES } from '../../constants';

export default {
    components: {
        SelectBox,
    },

    mixins: [mappingState],

    data() {
        return {
            roomProfiles: [],
            roomProfilesCopy: [],
            profilesCopy: {},
            profileSelectOptions: [],
            dataLoaded: false,
            incorrectAA: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['unsavedChanges']),

        filteredRoomProfiles() {
            if (this.searchKeyword) {
                let filtered = this.roomProfiles.filter(item => {
                    return item.name.toLowerCase().indexOf(this.searchKeyword) !== -1;
                });

                return sortLocations(filtered);
            }

            return sortLocations(this.roomProfiles);
        },

        filteredRoomProfilesCopy() {
            let keyword = this.searchKeyword;
            return keyword
                ? sortLocations(
                      this.roomProfilesCopy.some(item => item.name.toLowerCase().indexOf(keyword) > -1)
                          ? this.roomProfilesCopy.filter(
                                item => item.name.toLowerCase().indexOf(keyword) > -1
                            )
                          : this.roomProfilesCopy
                  )
                : sortLocations(this.roomProfilesCopy);
        },
    },

    watch: {
        roomProfiles: {
            handler(val) {
                if (!isEqual(val, this.roomProfilesCopy)) {
                    this.setUnsavedChanges(true);
                }
            },
            deep: true,
        },
    },

    created() {
        this.configButton({
            buttonName: 'saveButton',
            action: () => {
                this.saveRoomProfiles();
            },
            disabled: () => !this.unsavedChanges,
        });
        this.updateData(() => {
            return this.loadData();
        });
        return this.loadData();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        loadData() {
            this.showLoading(true);
            this.dataLoaded = false;
            this.incorrectAA = false;
            return this.loadResidentProfiles()
                .then(this.loadRoomProfiles)
                .then(() => {
                    this.showLoading(false);
                    if (this.userType === USER_TYPES.SUPER_USER && this.incorrectAA) {
                        this.roomProfiles = [];
                        this.roomProfilesCopy = [];
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t('error.common.forbidden'),
                        });
                    } else {
                        this.dataLoaded = true;
                    }
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            this.dataLoaded = true;
                            this.setSearchEnabled(this.roomProfilesCopy && this.roomProfilesCopy.length > 0);
                            errorHandler(response.status, ROUTE_NAMES.SENSORS__ROOM_PROFILES);
                        },
                    })
                )
                .then(() => {
                    this.setUnsavedChanges(false);
                });
        },

        loadResidentProfiles() {
            let def = [
                {
                    name: this.$t('sensors.roomProfilesTab.noProfile'),
                    value: null,
                },
            ];
            this.profilesCopy = {
                0: this.$t('sensors.roomProfilesTab.noProfile'),
            };
            this.showLoading(true);

            return resourceResidentProfiles({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                let residentProfiles = response.data ? response.data : [];
                if (!!residentProfiles && residentProfiles[0].assignmentarea_id !== this.userAreaId) {
                    this.incorrectAA = true;
                } else {
                    this.profileSelectOptions = [];
                    for (let i = 0; i < residentProfiles.length; i++) {
                        this.profileSelectOptions.push({
                            name: residentProfiles[i].name,
                            value: residentProfiles[i],
                        });
                        this.profilesCopy[residentProfiles[i].sensor_profile_id] = residentProfiles[i].name;
                    }

                    this.profileSelectOptions = def.concat(sortLocations(this.profileSelectOptions));
                }
            });
        },

        loadRoomProfiles() {
            this.roomProfiles = [];
            this.roomProfilesCopy = [];
            return resourceRoomProfiles({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                if (response.data) {
                    let data = response.data;
                    for (let i = 0; i < data.length; i++) {
                        if (data[i].sensor_profile) {
                            let profile = this.profileSelectOptions.find(option => {
                                return (
                                    option.value &&
                                    option.value.sensor_profile_id ===
                                        data[i].sensor_profile.sensor_profile_id
                                );
                            });
                            data[i].sensor_profile = profile ? profile.value : null;
                        }
                        this.roomProfiles.push(data[i]);
                    }
                    this.copyRoomProfilesArray();
                }
                this.setSearchEnabled(this.roomProfilesCopy && this.roomProfilesCopy.length > 0);
            });
        },

        copyRoomProfilesArray() {
            this.roomProfilesCopy = this.roomProfiles.map(item => ({
                ...item,
            }));
        },

        saveRoomProfiles() {
            this.showLoading(true);
            let roomProfile = this.roomProfiles.map(room => ({
                location_id: room.location_id,
                sensor_profile_id: room.sensor_profile ? room.sensor_profile.sensor_profile_id : null,
            }));

            return resourceRoomProfiles({ method: 'post', data: JSON.stringify(roomProfile) })
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.SENSORS__ROOM_PROFILES) {
                        this.setUnsavedChanges(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                        this.showLoading(false);
                        this.copyRoomProfilesArray();
                    }
                })
                .catch(ifIsNotCanceled());
        },
    },
};
</script>
